// Insight VARIABLES — Structure's Variables panel, scoped per formula column.
//
// Structure lets a formula declare named values (a field, or an expression) and
// reuse them. Only the formula TEXT migrated from Jira, so names like
// `zarvalue`, `ArrivalDate` or `COSPerc` arrived with nothing behind them and
// every formula reading one rendered blank.
//
// The binding is DELIBERATELY explicit rather than guessed — the the archive client formulas
// that use these names feed financial columns (Actual COS, Project Revenue),
// and a silently wrong number there is worse than a blank one.
//
// A variable is itself an expression, so a bare field reference
// (`issuezarvalueexclvat`) and a computed value (`round(cos / rev * 100, 1)`)
// are the same mechanism.
//
// WHAT THIS RETURNS, AND WHY IT IS NOT A VALUE
// --------------------------------------------
// A variable resolves against THE ROW IT IS READ ON, which is not known here.
// `sum { zarvalue }` visits every descendant and must read each one's own value;
// binding the expression to the root at build time would hand every iteration
// the root's value instead — for a container root with no fields of its own,
// that is a silent zero on exactly the columns that matter most.
//
// So this returns a scope of MARKERS, not values: `{ __exprVar: true, ast }`.
// resolveField in the interpreter spots the marker and evaluates the AST
// against whatever item is current, memoised per (item, marker). Same idiom the
// interpreter already uses for user-defined functions (`__fn`).
//
// Shared by BOTH evaluators — the browser (expr/index.js) and the server
// (folders/server/dsl-eval.js) — so they cannot drift.

// Must match the interpreter's own VARKEY, so WITH bindings and variables share
// one namespace and a WITH can shadow a variable.
const VARKEY = (n) => String(n).toUpperCase();

// `engine` is { parse } — passed in because the server loads the engine through
// a cached dynamic import and the browser imports it directly.
export function buildVariableScope(variables, engine) {
  const list = Array.isArray(variables)
    ? variables.filter((v) => v && v.name && String(v.formula || '').trim())
    : [];
  if (!list.length) return null;

  const { parse } = engine;
  const scope = Object.create(null); // null-prototype: no Object.prototype leakage
  for (const v of list) {
    const key = VARKEY(v.name);
    if (Object.prototype.hasOwnProperty.call(scope, key)) continue; // first wins
    let ast;
    try {
      ast = parse(v.formula);
    } catch {
      continue; // an unparseable binding leaves the name unbound, i.e. blank
    }
    scope[key] = { __exprVar: true, ast, name: v.name };
  }
  return Object.keys(scope).length ? scope : null;
}

// Stable identity for a variable set — part of the result-cache key, so editing
// a binding cannot serve a value computed under the previous one.
export function variablesSignature(variables) {
  return Array.isArray(variables) && variables.length
    ? variables.map((v) => `${v?.name}=${v?.formula}`).join('')
    : '';
}
